import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subject } from 'rxjs';

import { Customer } from '../../../models/customer.model';

export interface CustomerInfoEditDialogData {
  customer: Customer;
}

export interface CustomerInfoEditResult {
  customer: Partial<Customer>;
}

@Component({
  selector: 'app-customer-info-edit-dialog',
  templateUrl: './customer-info-edit-dialog.component.html',
  styleUrls: ['./customer-info-edit-dialog.component.scss']
})
export class CustomerInfoEditDialogComponent implements OnInit, OnDestroy {
  customerForm: FormGroup;
  loading = false;
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<CustomerInfoEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CustomerInfoEditDialogData
  ) {
    this.createForm();
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private createForm(): void {
    this.customerForm = this.fb.group({
      name: ['', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(100)
      ]],
      contactNo: ['', [
        Validators.required,
        Validators.pattern(/^[0-9+\-\s()]+$/)
      ]],
      email: ['', [
        Validators.required,
        Validators.email
      ]],
      gstNo: [''],
      panNo: ['']
    });
  }

  private initializeForm(): void {
    if (this.data.customer) {
      this.customerForm.patchValue({
        name: this.data.customer.name,
        contactNo: this.data.customer.contactNo,
        email: this.data.customer.email,
        gstNo: this.data.customer.gstNo || '',
        panNo: this.data.customer.panNo || ''
      });
    }
  }

  onSave(): void {
    if (this.customerForm.valid) {
      this.loading = true;
      
      const formValue = this.customerForm.value;
      const result: CustomerInfoEditResult = {
        customer: {
          name: formValue.name,
          contactNo: formValue.contactNo,
          email: formValue.email,
          gstNo: formValue.gstNo || null,
          panNo: formValue.panNo || null
        }
      };

      this.dialogRef.close(result);
    } else {
      this.markFormGroupTouched(this.customerForm);
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  getFieldError(fieldName: string): string {
    const control = this.customerForm.get(fieldName);
    if (control && control.errors && control.touched) {
      if (control.errors['required']) {
        return `${this.getFieldLabel(fieldName)} is required`;
      }
      if (control.errors['minlength']) {
        return `${this.getFieldLabel(fieldName)} must be at least ${control.errors['minlength'].requiredLength} characters`;
      }
      if (control.errors['maxlength']) {
        return `${this.getFieldLabel(fieldName)} must not exceed ${control.errors['maxlength'].requiredLength} characters`;
      }
      if (control.errors['email']) {
        return 'Please enter a valid email address';
      }
      if (control.errors['pattern']) {
        return `Please enter a valid ${this.getFieldLabel(fieldName)}`;
      }
    }
    return '';
  }

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      name: 'Customer name',
      contactNo: 'Contact number',
      email: 'Email address',
      gstNo: 'GST number',
      panNo: 'PAN number'
    };
    return labels[fieldName] || fieldName;
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control && control.markAsTouched();
    });
  }
}